/**
 * AddonSyncTab - Tab de sincronização com Stripe
 * Permite verificar e sincronizar add-ons do Stripe
 * Inclui detecção e correção de add-ons órfãos (sem provider_instance)
 * 
 * @version 1.2
 * @date 2025-12-29
 * @update Adicionada funcionalidade de detecção e correção de add-ons órfãos
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Command, 
  CommandEmpty, 
  CommandGroup, 
  CommandInput, 
  CommandItem, 
  CommandList 
} from '@/components/ui/command';
import { 
  RefreshCw, 
  Search, 
  CheckCircle, 
  AlertTriangle,
  ExternalLink,
  Loader2,
  Package,
  Link2,
  Link2Off,
  Copy,
  Check,
  ChevronsUpDown,
  ServerCrash,
  Wrench
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAddonManagement, useTenantsList } from '@/hooks/useAddonManagement';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { logger } from '@/lib/logger';

/**
 * Interface para add-on local detalhado
 */
interface LocalAddonDetail {
  id: string;
  addonId: string;
  addonName: string;
  addonType: string | null;
  priceMonthly: number | null;
  stripeProductId: string | null;
  stripeSubscriptionItemId: string | null;
  stripeInvoiceId: string | null;
  status: string;
  quantity: number;
  activatedAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  hasStripeLink: boolean;
}

/**
 * Interface para add-on órfão (sem provider_instance)
 */
interface OrphanAddon {
  tenant_addon_id: string;
  tenant_id: string;
  tenant_name: string;
  addon_id: string;
  addon_name: string;
  provider_id: string;
  provider_name: string;
  provider_slug: string;
  stripe_subscription_item_id: string | null;
  status: string;
  created_at: string;
}

/**
 * Interface para resultado da verificação
 */
interface CheckResult {
  tenant: {
    id: string;
    name: string;
    stripe_customer_id: string | null;
  };
  stripeSubscriptions: Array<{
    id: string;
    status: string;
    items: Array<{
      id: string;
      productName: string;
      quantity: number;
    }>;
  }>;
  localAddons: LocalAddonDetail[];
  missingInLocal: Array<{
    subscriptionId: string;
    subscriptionItemId: string;
    productName: string;
    quantity: number;
  }>;
  orphanedLocal: Array<{
    id: string;
    addonName: string;
  }>;
}

/**
 * Extrai mensagem de erro de forma robusta
 * Supabase retorna objetos com message/details/hint, não instanceof Error
 */
function extractErrorMessage(error: unknown): string {
  if (!error) return 'Erro desconhecido';
  
  // Se for Error padrão
  if (error instanceof Error) return error.message;
  
  // Se for objeto (erro do Supabase)
  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;
    // Tenta extrair message, details ou hint
    if (typeof err.message === 'string' && err.message) return err.message;
    if (typeof err.details === 'string' && err.details) return err.details;
    if (typeof err.hint === 'string' && err.hint) return err.hint;
    // Fallback: stringify
    try {
      return JSON.stringify(error);
    } catch {
      return 'Erro desconhecido';
    }
  }
  
  return String(error);
}

/**
 * Formata valor monetário em BRL
 */
function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Formata data para exibição
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return '-';
  }
}

/**
 * Retorna badge de status com cor apropriada
 */
function getStatusBadge(status: string) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    active: 'default',
    pending: 'secondary',
    canceled: 'destructive',
    expired: 'outline',
  };
  
  const labels: Record<string, string> = {
    active: 'Ativo',
    pending: 'Pendente',
    canceled: 'Cancelado',
    expired: 'Expirado',
  };
  
  return (
    <Badge variant={variants[status] || 'outline'}>
      {labels[status] || status}
    </Badge>
  );
}

/**
 * Retorna badge de tipo de add-on
 */
function getTypeBadge(type: string | null) {
  if (!type) return null;
  
  const labels: Record<string, string> = {
    inbox: 'Inbox',
    feature: 'Feature',
    limit: 'Limite',
  };
  
  return (
    <Badge variant="outline" className="text-xs">
      {labels[type] || type}
    </Badge>
  );
}

export function AddonSyncTab() {
  const { t } = useTranslation('admin');
  const { syncWithStripe, refetchAll } = useAddonManagement();
  const { data: tenants, isLoading: isLoadingTenants } = useTenantsList();
  
  // Estados
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);
  const [open, setOpen] = useState(false); // Estado para controlar Combobox
  
  // Estados para add-ons órfãos (sem provider_instance)
  const [orphanAddons, setOrphanAddons] = useState<OrphanAddon[]>([]);
  const [isCheckingOrphans, setIsCheckingOrphans] = useState(false);
  const [isFixingOrphan, setIsFixingOrphan] = useState<string | null>(null);

  /**
   * Verifica add-ons do tenant no Stripe
   */
  const handleCheck = async () => {
    if (!selectedTenant) {
      toast.error('Selecione um tenant');
      return;
    }

    setIsChecking(true);
    setCheckResult(null);

    try {
      const result = await syncWithStripe(selectedTenant);
      setCheckResult(result as CheckResult);
      
      if (result.missingInLocal?.length === 0 && result.orphanedLocal?.length === 0) {
        toast.success('Tudo sincronizado! Nenhuma discrepância encontrada.');
      } else {
        toast.warning(`Encontradas ${result.missingInLocal?.length || 0} pendências`);
      }
    } catch (error: unknown) {
      // Extrai mensagem de erro de forma robusta (Supabase retorna objeto, não Error)
      const errorMessage = extractErrorMessage(error);
      toast.error(`Erro ao verificar: ${errorMessage}`);
      logger.error('AddonSyncTab', 'Erro handleCheck', error);
    } finally {
      setIsChecking(false);
    }
  };

  /**
   * Sincroniza uma subscription específica
   */
  const handleSync = async (subscriptionId: string) => {
    if (!selectedTenant) return;

    setIsSyncing(subscriptionId);

    try {
      await syncWithStripe(selectedTenant, subscriptionId);
      toast.success('Sincronização concluída!');
      
      // Refaz a verificação
      await handleCheck();
      refetchAll();
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error);
      toast.error(`Erro ao sincronizar: ${errorMessage}`);
      logger.error('AddonSyncTab', 'Erro handleSync', error);
    } finally {
      setIsSyncing(null);
    }
  };

  /**
   * Copia ID para clipboard
   */
  const handleCopyId = (id: string, label: string) => {
    navigator.clipboard.writeText(id);
    toast.success(`${label} copiado!`);
  };

  /**
   * Verifica add-ons órfãos (sem provider_instance) globalmente ou por tenant
   */
  const handleCheckOrphans = async () => {
    setIsCheckingOrphans(true);
    setOrphanAddons([]);

    try {
      logger.info('AddonSyncTab', 'Verificando add-ons órfãos...');
      
      // Chama a RPC passando tenant_hint se selecionado
      const { data, error } = await supabase.rpc('fn_check_orphan_addon_instances', {
        p_tenant_hint: selectedTenant || null
      });

      if (error) {
        logger.error('AddonSyncTab', 'Erro ao verificar órfãos', error);
        throw error;
      }

      const orphans = (data || []) as OrphanAddon[];
      setOrphanAddons(orphans);

      if (orphans.length === 0) {
        toast.success('Nenhum add-on órfão encontrado!');
      } else {
        toast.warning(`Encontrados ${orphans.length} add-ons sem provider_instance`);
      }

      logger.info('AddonSyncTab', `${orphans.length} add-ons órfãos encontrados`);
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error);
      toast.error(`Erro ao verificar órfãos: ${errorMessage}`);
      logger.error('AddonSyncTab', 'Erro handleCheckOrphans', error);
    } finally {
      setIsCheckingOrphans(false);
    }
  };

  /**
   * Corrige um add-on órfão criando a provider_instance
   */
  const handleFixOrphan = async (tenantAddonId: string) => {
    setIsFixingOrphan(tenantAddonId);

    try {
      logger.info('AddonSyncTab', `Corrigindo add-on órfão: ${tenantAddonId}`);
      
      const { data, error } = await supabase.rpc('fn_fix_orphan_addon_instances', {
        p_tenant_addon_id: tenantAddonId
      });

      if (error) {
        logger.error('AddonSyncTab', 'Erro ao corrigir órfão', error);
        throw error;
      }

      toast.success('Provider instance criada com sucesso!');
      logger.info('AddonSyncTab', `Provider instance criada: ${data}`);

      // Atualiza a lista de órfãos
      await handleCheckOrphans();
      refetchAll();
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error);
      toast.error(`Erro ao corrigir: ${errorMessage}`);
      logger.error('AddonSyncTab', 'Erro handleFixOrphan', error);
    } finally {
      setIsFixingOrphan(null);
    }
  };

  /**
   * Corrige todos os add-ons órfãos de uma vez
   */
  const handleFixAllOrphans = async () => {
    if (orphanAddons.length === 0) return;

    const confirmFix = window.confirm(
      `Deseja criar provider_instances para ${orphanAddons.length} add-ons órfãos?`
    );

    if (!confirmFix) return;

    let successCount = 0;
    let errorCount = 0;

    for (const orphan of orphanAddons) {
      try {
        const { error } = await supabase.rpc('fn_fix_orphan_addon_instances', {
          p_tenant_addon_id: orphan.tenant_addon_id
        });

        if (error) throw error;
        successCount++;
      } catch (error) {
        errorCount++;
        logger.error('AddonSyncTab', `Erro ao corrigir ${orphan.tenant_addon_id}`, error);
      }
    }

    if (errorCount === 0) {
      toast.success(`${successCount} provider instances criadas com sucesso!`);
    } else {
      toast.warning(`${successCount} criadas, ${errorCount} falharam`);
    }

    await handleCheckOrphans();
    refetchAll();
  };

  return (
    <div className="space-y-4">
      {/* Card de seleção de tenant */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('addonManagement.sync.title', 'Sincronização com Stripe')}
          </CardTitle>
          <CardDescription>
            {t('addonManagement.sync.description', 'Verifique e sincronize add-ons contratados no Stripe que não estão registrados localmente')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Combobox com busca para seleção de tenant */}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="flex-1 justify-between"
                  disabled={isLoadingTenants}
                >
                  {selectedTenant ? (
                    tenants?.find(t => t.id === selectedTenant)?.name || 'Selecione um tenant...'
                  ) : (
                    <span className="text-muted-foreground">Selecione um tenant...</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar tenant por nome..." />
                  <CommandList>
                    <CommandEmpty>Nenhum tenant encontrado.</CommandEmpty>
                    <CommandGroup>
                      {tenants?.map((tenant) => (
                        <CommandItem
                          key={tenant.id}
                          value={tenant.name}
                          onSelect={() => {
                            setSelectedTenant(tenant.id);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedTenant === tenant.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <span>{tenant.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Button 
              onClick={handleCheck} 
              disabled={!selectedTenant || isChecking}
            >
              {isChecking ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              {t('addonManagement.sync.checkButton', 'Verificar no Stripe')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resultado da verificação */}
      {checkResult && (
        <>
          {/* Info do tenant */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Tenant: {checkResult.tenant.name}
                {checkResult.tenant.stripe_customer_id ? (
                  <Badge variant="secondary">
                    Stripe ID: {checkResult.tenant.stripe_customer_id.slice(0, 15)}...
                  </Badge>
                ) : (
                  <Badge variant="destructive">Sem Stripe Customer ID</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Subscriptions no Stripe:</span>
                  <p className="font-medium">{checkResult.stripeSubscriptions.length}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Add-ons locais:</span>
                  <p className="font-medium">{checkResult.localAddons.length}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Faltando no BD:</span>
                  <p className="font-medium text-amber-600">{checkResult.missingInLocal.length}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Órfãos locais:</span>
                  <p className="font-medium text-red-600">{checkResult.orphanedLocal.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lista de add-ons locais detalhados */}
          {checkResult.localAddons.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Add-ons Locais Registrados ({checkResult.localAddons.length})
                </CardTitle>
                <CardDescription>
                  Lista detalhada dos add-ons registrados no banco de dados local
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {checkResult.localAddons.map((addon) => (
                    <div 
                      key={addon.id}
                      className="p-4 rounded-lg border bg-card"
                    >
                      {/* Linha 1: Nome e status */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{addon.addonName}</span>
                          {getTypeBadge(addon.addonType)}
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(addon.status)}
                          {addon.hasStripeLink ? (
                            <Link2 className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Link2Off className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      
                      {/* Linha 2: Detalhes */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-muted-foreground mb-2">
                        <div>
                          <span className="text-xs uppercase">Quantidade</span>
                          <p className="font-medium text-foreground">{addon.quantity}</p>
                        </div>
                        <div>
                          <span className="text-xs uppercase">Valor Mensal</span>
                          <p className="font-medium text-foreground">{formatCurrency(addon.priceMonthly)}</p>
                        </div>
                        <div>
                          <span className="text-xs uppercase">Ativado em</span>
                          <p className="font-medium text-foreground">{formatDate(addon.activatedAt)}</p>
                        </div>
                        <div>
                          <span className="text-xs uppercase">Período Atual</span>
                          <p className="font-medium text-foreground">
                            {addon.currentPeriodStart && addon.currentPeriodEnd 
                              ? `${formatDate(addon.currentPeriodStart)} - ${formatDate(addon.currentPeriodEnd)}`
                              : '-'
                            }
                          </p>
                        </div>
                      </div>
                      
                      {/* Linha 3: IDs do Stripe e ações */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t text-xs">
                        <div className="flex flex-wrap gap-2 text-muted-foreground">
                          {addon.stripeSubscriptionItemId && (
                            <span className="flex items-center gap-1">
                              <span className="font-medium">Item:</span>
                              <code className="bg-muted px-1 rounded">
                                {addon.stripeSubscriptionItemId.slice(0, 20)}...
                              </code>
                            </span>
                          )}
                          {addon.stripeInvoiceId && (
                            <span className="flex items-center gap-1">
                              <span className="font-medium">Invoice:</span>
                              <code className="bg-muted px-1 rounded">
                                {addon.stripeInvoiceId.slice(0, 15)}...
                              </code>
                            </span>
                          )}
                          {!addon.stripeSubscriptionItemId && !addon.stripeInvoiceId && (
                            <span className="text-amber-600">Sem vínculo com Stripe</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {addon.stripeSubscriptionItemId && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => handleCopyId(addon.stripeSubscriptionItemId!, 'ID do item')}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => window.open(`https://dashboard.stripe.com/subscription_items/${addon.stripeSubscriptionItemId}`, '_blank')}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de pendências */}
          {checkResult.missingInLocal.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Compras no Stripe não registradas localmente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {checkResult.missingInLocal.map((item, index) => (
                    <div 
                      key={`${item.subscriptionId}-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                    >
                      <div>
                        <p className="font-medium">{item.productName}</p>
                        <p className="text-sm text-muted-foreground">
                          Qtd: {item.quantity} | Subscription: {item.subscriptionId.slice(0, 20)}...
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`https://dashboard.stripe.com/subscriptions/${item.subscriptionId}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSync(item.subscriptionId)}
                          disabled={isSyncing === item.subscriptionId}
                        >
                          {isSyncing === item.subscriptionId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-1" />
                          )}
                          Sincronizar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sucesso - tudo sincronizado */}
          {checkResult.missingInLocal.length === 0 && checkResult.orphanedLocal.length === 0 && (
            <Alert>
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              <AlertTitle>Tudo sincronizado!</AlertTitle>
              <AlertDescription>
                Não há discrepâncias entre o Stripe e o banco de dados local para este tenant.
              </AlertDescription>
            </Alert>
          )}

          {/* Órfãos locais (sem vínculo Stripe) */}
          {checkResult.orphanedLocal.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Add-ons órfãos detectados</AlertTitle>
              <AlertDescription>
                Os seguintes add-ons existem no BD mas não têm correspondente no Stripe: {' '}
                {checkResult.orphanedLocal.map(o => o.addonName).join(', ')}
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Seção de Add-ons sem Provider Instance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ServerCrash className="h-5 w-5 text-amber-500" />
            Add-ons Órfãos (sem Provider Instance)
          </CardTitle>
          <CardDescription>
            Detecta add-ons do tipo inbox que não possuem provider_instance correspondente.
            Esses add-ons foram registrados mas não tiveram suas instâncias criadas corretamente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <Button 
              onClick={handleCheckOrphans} 
              disabled={isCheckingOrphans}
              variant="outline"
            >
              {isCheckingOrphans ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              {selectedTenant 
                ? 'Verificar Órfãos do Tenant' 
                : 'Verificar Todos os Órfãos'
              }
            </Button>
            
            {orphanAddons.length > 0 && (
              <Button 
                onClick={handleFixAllOrphans}
                variant="default"
              >
                <Wrench className="h-4 w-4 mr-2" />
                Corrigir Todos ({orphanAddons.length})
              </Button>
            )}
          </div>

          {/* Lista de add-ons órfãos */}
          {orphanAddons.length > 0 && (
            <div className="space-y-3">
              {orphanAddons.map((orphan) => (
                <div 
                  key={orphan.tenant_addon_id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{orphan.addon_name}</span>
                      <Badge variant="outline">{orphan.provider_name}</Badge>
                      <Badge variant="secondary">{orphan.provider_slug}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-medium">Tenant:</span> {orphan.tenant_name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Criado em: {formatDate(orphan.created_at)}
                      {orphan.stripe_subscription_item_id && (
                        <span className="ml-2">
                          | Item: {orphan.stripe_subscription_item_id.slice(0, 20)}...
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleFixOrphan(orphan.tenant_addon_id)}
                    disabled={isFixingOrphan === orphan.tenant_addon_id}
                  >
                    {isFixingOrphan === orphan.tenant_addon_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wrench className="h-4 w-4 mr-1" />
                    )}
                    Criar Instância
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Mensagem quando não há órfãos */}
          {orphanAddons.length === 0 && !isCheckingOrphans && (
            <div className="text-center text-muted-foreground py-4">
              Clique em "Verificar" para detectar add-ons sem provider_instance
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
